import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from './RichTextEditor';
import ConfirmModal from './ConfirmModal';

const TaskMessages = ({ taskId, isTaskEditor }) => {
  const { currentLang, token, API_URL, getCsrfToken, user } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [deletingMessage, setDeletingMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, [taskId, pagination.page]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  useEffect(() => {
    if (shouldScrollToBottom && !isInitialLoad) {
    scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [messages, shouldScrollToBottom, isInitialLoad]);

  useEffect(() => {
    if (messages.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [messages, isInitialLoad]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/messages?page=${pagination.page}&limit=${pagination.limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessages(data.data.messages || []);
        setPagination(data.data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
          has_next: false,
          has_prev: false
        });
      } else {
        throw new Error(data.message || 'Ошибка загрузки сообщений');
      }
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    setError('');

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          text: newMessage.trim(),
          status: 0
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewMessage('');
        // Перезагружаем сообщения с первой страницы
        setPagination(prev => ({ ...prev, page: 1 }));
        setShouldScrollToBottom(true);
        await fetchMessages();
        showNotification(
          currentLang === 'ru' ? 'Сообщение отправлено!' : 'Message sent!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка отправки сообщения');
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      setError(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = async (messageId, newText) => {
    if (!newText.trim()) return;

    setEditingMessage(null);
    setEditText('');

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          text: newText.trim(),
          status: 0
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShouldScrollToBottom(true);
        await fetchMessages();
        showNotification(
          currentLang === 'ru' ? 'Сообщение обновлено!' : 'Message updated!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка обновления сообщения');
      }
    } catch (error) {
      console.error('Ошибка обновления сообщения:', error);
      setError(error.message);
    }
  };

  const showDeleteConfirm = (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!messageToDelete) return;

    setDeletingMessage(messageToDelete);

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/messages/${messageToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShouldScrollToBottom(true);
        await fetchMessages();
        showNotification(
          currentLang === 'ru' ? 'Сообщение удалено!' : 'Message deleted!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка удаления сообщения');
      }
    } catch (error) {
      console.error('Ошибка удаления сообщения:', error);
      setError(error.message);
    } finally {
      setDeletingMessage(null);
      setMessageToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  const startEditing = (message) => {
    setEditingMessage(message.id);
    setEditText(message.text);
  };

  const cancelEditing = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString('ru-RU', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else {
        return date.toLocaleDateString('ru-RU', { 
          day: '2-digit', 
          month: '2-digit',
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const isOwnMessage = (message) => {
    return user?.id === message.uid;
  };



  return (
    <div className="flex flex-col h-full">
      {/* Заголовок */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {currentLang === 'ru' ? 'Сообщения к задаче' : 'Task Messages'}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {currentLang === 'ru' 
            ? 'Обсудите детали задачи с командой' 
            : 'Discuss task details with your team'
          }
        </p>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Чат */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Сообщения */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto space-y-4 mb-4 chat-container"
        >
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">
                {currentLang === 'ru' 
                  ? 'Пока нет сообщений. Будьте первым!' 
                  : 'No messages yet. Be the first!'
                }
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div 
                key={message.id} 
                className={`message-enter ${message.status === 0 ? 'message-unread' : 'message-read'}`}
              >
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  {/* Заголовок сообщения */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white text-sm font-medium message-avatar">
                        {message.user_name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{message.user_name}</div>
                        <div className="text-xs text-gray-500">{formatDate(message.created_at)}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {message.status === 0 && !isOwnMessage(message) && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-medium message-new-badge">
                          {currentLang === 'ru' ? 'Новое' : 'New'}
                        </span>
                      )}
                      {isOwnMessage(message) && (
                        <div className="flex space-x-1 message-actions">
                          <button
                            onClick={() => startEditing(message)}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                            title={currentLang === 'ru' ? 'Редактировать' : 'Edit'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => showDeleteConfirm(message.id)}
                            disabled={deletingMessage === message.id}
                            className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                            title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                          >
                            {deletingMessage === message.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Содержимое сообщения */}
                  {editingMessage === message.id ? (
                    <div className="space-y-3">
                      <RichTextEditor
                        value={editText}
                        onChange={setEditText}
                        currentLang={currentLang}
                        placeholder={currentLang === 'ru' ? 'Редактируйте сообщение...' : 'Edit message...'}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditMessage(message.id, editText)}
                          className="px-3 py-1 text-sm bg-brand-500 text-white rounded-md hover:bg-brand-600"
                        >
                          {currentLang === 'ru' ? 'Сохранить' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="px-3 py-1 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        >
                          {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-black whitespace-pre-wrap leading-relaxed message-content">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: message.text }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Пагинация */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center space-x-2 mb-4">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={!pagination.has_prev}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentLang === 'ru' ? '← Назад' : '← Previous'}
            </button>
            <span className="text-sm text-gray-600">
              {currentLang === 'ru' 
                ? `Страница ${pagination.page} из ${pagination.pages}` 
                : `Page ${pagination.page} of ${pagination.pages}`
              }
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={!pagination.has_next}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentLang === 'ru' ? 'Вперед →' : 'Next →'}
            </button>
          </div>
        )}
        
        {/* Форма отправки */}
        {isTaskEditor && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {currentLang === 'ru' ? 'Новое сообщение' : 'New Message'}
            </label>
            <RichTextEditor
              value={newMessage}
              onChange={setNewMessage}
              currentLang={currentLang}
              placeholder={currentLang === 'ru' ? 'Напишите сообщение...' : 'Write a message...'}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{currentLang === 'ru' ? 'Отправка...' : 'Sending...'}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>{currentLang === 'ru' ? 'Отправить' : 'Send'}</span>
                </div>
              )}
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить это сообщение? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this message? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default TaskMessages; 